import { useTranslation } from 'next-i18next';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import type { IgeRecommendationResults } from './useIgeRecommendation';
import { EXTERNAL_LINKS } from '@hub/libs/redirect.helper';
import { useEffect } from 'react';
import { useWidenAsset } from '../../../../common/Widen/widenAsset';
import { formatPrice } from '@hub/libs/product-price-utils';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';

type IgeRecommendationTileProps = {
  igeResult: IgeRecommendationResults;
  fileDimensions?: Record<string, number | string>;
  session: Session;
};

export const IgeRecommendationTile = ({ igeResult, fileDimensions, session }: IgeRecommendationTileProps) => {
  const { t } = useTranslation();
  const { nameKey, data, technicalDrawingWidenId } = igeResult;
  const { articlesCount, articleNumber, shopUrl, unitPrice, widenImageId } = { ...data };
  const { getWidenAsset, widenAsset } = useWidenAsset();
  const gearParamsWithoutUnit = ['hub_design', 'module', 'number_of_teeth'];
  const router = useRouter();
  const locale = getLocale(router);

  useEffect(() => {
    if (data && data.articleNumber && data.widenImageId) {
      void getWidenAsset(data.widenImageId);
    }
  }, [data.widenImageId]);

  const getHubTranslation = (value: string | number) => {
    if (typeof value === 'number') {
      return value;
    }
    return t(`COMMON.GEARS.${value.toUpperCase().replace('-', '_')}`, { ns: 'free-form-hub/freeFormHubCommon' });
  };

  const nbsShopUrl = () => {
    const defaultShopUrl = EXTERNAL_LINKS.SHOP_HOMEPAGE(locale) + shopUrl;
    const url = new URL(articleNumber ? shopUrl : defaultShopUrl);
    const parameters = new URLSearchParams(url.search);
    parameters.set('utm_media', 'cadiframeclick');
    parameters.set('utm_campaign', 'hmishopclick_shop');
    url.search = parameters.toString();
    return url.toString();
  };

  const shapeNameSection = (
    <div>
      <h4 className="mb-6 text-2xl">{t(`COMMON.FILE_UPLOAD.TITLE`, { ns: 'free-form-hub/freeFormHubCommon' })}</h4>
      <div className="text-center font-medium text-2xl mt-4">
        <span>{t(`COMMON.FILE_UPLOAD.RECOGNIZED_PRODUCT`, { ns: 'free-form-hub/freeFormHubCommon' })}</span>
        &nbsp;
        <span>{t(`COMMON.GEARS.${nameKey}`, { ns: 'free-form-hub/freeFormHubCommon' })}</span>
      </div>
    </div>
  );

  const dimensionsSection = fileDimensions && (
    <div className="flex flex-col md:flex-row justify-center	mt-10">
      <div className="mr-0 md:mr-10 max-h-[190px] overflow-hidden">
        <WidenImage
          shortCode={technicalDrawingWidenId}
          title={t(`COMMON.GEARS.${nameKey}`, { ns: 'free-form-hub/freeFormHubCommon' })}
          alt={t(`COMMON.GEARS.${nameKey}`, { ns: 'free-form-hub/freeFormHubCommon' })}
          filename={technicalDrawingWidenId}
          width={400}
          height={300}
        />
      </div>
      <div className="flex flex-col">
        <div className="mt-2 mb-1.5">
          <h4 className="text-[15px]">
            {t(`COMMON.FILE_UPLOAD.DIMENSIONS`, { ns: 'free-form-hub/freeFormHubCommon' })}
          </h4>
        </div>
        <ul className="block">
          {Object.keys(fileDimensions).map(key => (
            <li className="text-xs" key={key}>
              <span className="font-bold">
                {gearParamsWithoutUnit.includes(key)
                  ? t(`COMMON.GEARS.${key.toLocaleUpperCase()}`, { ns: 'free-form-hub/freeFormHubCommon' })
                  : key}
                :{' '}
              </span>
              {!gearParamsWithoutUnit.includes(key)
                ? `${Number(fileDimensions[key])?.toFixed(2)} mm`
                : getHubTranslation(fileDimensions[key])}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const productTile = (
    <div className="flex flex-col items-center min-h-[325px] w-[234px] text-center shadow-2xl p-[15px] rounded-lg border-0">
      <div className="text-sm font-semibold leading-7 max-h-[55px] overflow-hidden">
        {t(fileDimensions ? 'COMMON.FILE_UPLOAD.IDENTICAL_PRODUCTS' : 'COMMON.FILE_UPLOAD.BROWSE', {
          count: articlesCount,
          ns: 'free-form-hub/freeFormHubCommon',
        })}
      </div>
      <div className="mt-[30px] min-h-[125px]">
        <WidenImage
          shortCode={widenAsset?.external_id ?? widenImageId}
          title={t(`COMMON.GEARS.${nameKey}`, { ns: 'free-form-hub/freeFormHubCommon' })}
          alt={t(`COMMON.GEARS.${nameKey}`, { ns: 'free-form-hub/freeFormHubCommon' })}
          filename={widenAsset?.external_id ?? widenImageId}
          width={175}
          height={85}
        />
        <div className="text-xs font-normal text-gray-800 mt-2">
          {t(`COMMON.GEARS.${nameKey}`, { ns: 'free-form-hub/freeFormHubCommon' })}
        </div>
      </div>
      {unitPrice?.value > 0 && (
        <>
          <div className="text-sm font-normal mt-4 whitespace-nowrap">
            {t(`COMMON.FILE_UPLOAD.FROM`, {
              ns: 'free-form-hub/freeFormHubCommon',
            })}
            <span className="text-orange-500 font-semibold">
              {' '}
              {formatPrice(unitPrice.value, locale, session.currency)}
            </span>
            {t(`COMMON.FILE_UPLOAD.PER_PIECE`, {
              ns: 'free-form-hub/freeFormHubCommon',
            })}
          </div>
          <div className="text-[10px] font-light text-gray-800">
            {t('COMMON.FILE_UPLOAD.BROWSE', {
              ns: 'free-form-hub/freeFormHubCommon',
            })}
          </div>
        </>
      )}
      <a
        id="cad-search-shop-link"
        className="min-w-[175px] mt-auto py-2 px-6 rounded-md border border-transparent text-base font-medium text-white bg-orange-500 hover:bg-orange-600"
        href={nbsShopUrl()}
        target="_blank"
        rel="noreferrer"
      >
        {t('COMMON.FILE_UPLOAD.GO_TO_IBH', { ns: 'free-form-hub/freeFormHubCommon' })}
      </a>
      <div className="text-xs font-medium text-availability-fast pt-[10px]">{unitPrice?.dtText}</div>
    </div>
  );

  return (
    <div>
      {shapeNameSection}
      {dimensionsSection}
      <h5 className="text-[16px] mt-2.5 mb-[20px] font-medium">
        {t(`COMMON.FILE_UPLOAD.RECOMMENDATION`, { ns: 'free-form-hub/freeFormHubCommon' })}
      </h5>
      <div className="flex justify-center gap-[30px]">{productTile}</div>
    </div>
  );
};
